/**
 * Cart Store - Multi-store cart management
 * Supports multiple store carts, each with their own items and checkout flow
 */

import { defineStore } from "pinia";
import api from "@/services/api";
import { getErrorMessage, isAuthError } from "@/utils/errorUtils";
import type {
  CartState,
  CartItem,
  StoreCart,
  CartSnapshot,
  AddItemPayload,
  UpdateQuantityPayload,
  CheckoutPayload,
  StagedItemChange,
} from "./types/cart.types";
import type { Instruction, DeliveryQuote } from "@/types";

const SNAPSHOT_KEY = "lyt-cart-snapshot";

/**
 * Build a stable signature for an instruction list so variants of the same PLU stay distinct.
 */
const instructionSignature = (instructions?: Instruction[]): string => {
  const normalized = (instructions || []).map((inst) => ({
    c: inst.CookingNum || "",
    m: inst.CookingMsg || inst.text || "",
    s: inst.StockCode || "",
    p: Number(inst.Price ?? 0) || 0,
    w: inst.Wait ?? false,
    q: Number(inst.Qty ?? inst.count ?? 1) || 1,
    pr: inst.Printer || "",
  }));
  normalized.sort((a, b) => {
    const aKey = `${a.c}|${a.m}|${a.s}|${a.p}|${a.w}|${a.q}|${a.pr}`;
    const bKey = `${b.c}|${b.m}|${b.s}|${b.p}|${b.w}|${b.q}|${b.pr}`;
    return aKey.localeCompare(bKey);
  });
  return JSON.stringify(normalized);
};

const buildPendingKey = (plu: string, instructions?: Instruction[]) =>
  `${plu || ""}::${instructionSignature(instructions)}`;

const mergeInstructionCounts = (
  existing: Instruction[] = [],
  incoming: Instruction[] = [],
  multiplier = 1
): Instruction[] => {
  const merged = new Map<string, Instruction>();
  const add = (inst: Instruction) => {
    const key = `${inst.CookingNum || ""}|${inst.CookingMsg || inst.text || ""}|${inst.StockCode || ""}`;
    const current = merged.get(key) || { ...inst };
    const incomingCount = (Number(inst.count ?? inst.Qty ?? 1) || 1) * (multiplier > 0 ? multiplier : 1);
    const currentCount = Number(current.count ?? current.Qty ?? 0) || 0;
    const nextCount = currentCount + incomingCount;
    merged.set(key, {
      ...current,
      ...inst,
      count: nextCount,
      Qty: nextCount,
    });
  };
  existing.forEach(add);
  incoming.forEach(add);
  return Array.from(merged.values());
};

const scaleInstructionCounts = (instructions: Instruction[] = [], ratio: number) => {
  if (!ratio || ratio <= 0) return [];
  return instructions.map((inst) => {
    const base = Number(inst.count ?? inst.Qty ?? 1) || 1;
    const next = Math.max(0, Math.round(base * ratio));
    return {
      ...inst,
      count: next,
      Qty: next,
    };
  });
};

/**
 * Normalize cart totals client-side to keep UX snappy and fees at zero
 */
function normalizeCart(cart: StoreCart | null): StoreCart | null {
  if (!cart) return null;

  const items = (cart.items || []).map((item) => {
    const quantity = Number(item.quantity) || 0;
    const unitPrice = Number(item.unitPrice) || 0;
    return {
      ...item,
      quantity,
      lineTotal: (unitPrice * quantity).toFixed(2),
    };
  });

  const subtotalNumber = items.reduce(
    (total, item) => total + (Number(item.lineTotal) || 0),
    0
  );
  const itemCount = items.reduce(
    (total, item) => total + (Number(item.quantity) || 0),
    0
  );

  const delivery = cart.summary?.delivery
    ? {
        ...cart.summary.delivery,
        fee: (cart.summary.delivery.fee as any)?.toString?.() ?? "0",
        freeEligible: false,
      }
    : {
        available: false,
        fee: "0",
        freeEligible: false,
        radiusKm: 0,
        preparationTimeMinutes: 0,
      };

  const summary = {
    ...cart.summary,
    itemCount,
    subtotal: subtotalNumber.toFixed(2),
    deliveryFee: (0).toFixed(2),
    totalAmount: subtotalNumber.toFixed(2),
    delivery,
    eligibleForFreeDelivery: false,
    freeDeliveryThreshold: (cart.summary?.freeDeliveryThreshold ?? 0).toString(),
    minimumOrderValue: (cart.summary?.minimumOrderValue ?? 0).toString(),
    amountLeftForFreeDelivery: (0).toString(),
  } as StoreCart["summary"];

  return {
    ...cart,
    items,
    summary,
  };
}

/**
 * Normalize instructions into the backend payload shape.
 */
const normalizeInstructionsForApi = (instructions?: Instruction[]): Instruction[] => {
  if (!instructions || instructions.length === 0) return [];
  return instructions.map((inst) => ({
    CookingNum: inst.CookingNum || "",
    StockCode: inst.StockCode || "",
    CookingMsg: inst.CookingMsg || inst.text || "",
    Printer: inst.Printer || "",
    Qty: inst.Qty ?? inst.count ?? 1,
    Price: inst.Price ?? 0,
    count: inst.count ?? 1,
    Wait: inst.Wait ?? false,
  }));
};

export const useCartStore = defineStore("cart", {
  state: (): CartState => ({
    carts: {},
    newItems: {},
    activeCartSlug: null,
    loading: {
      fetchAll: false,
      fetch: false,
      add: false,
      update: false,
      remove: false,
      clear: false,
      checkout: false,
      deliveryQuote: false,
    },
    error: {
      fetchAll: "",
      fetch: "",
      add: "",
      update: "",
      remove: "",
      clear: "",
      checkout: "",
      deliveryQuote: "",
    },
    pendingChanges: {} as Record<string, Record<string, StagedItemChange>>,
    persistedAt: null,
    commitPromises: {} as Record<string, Promise<StoreCart | null>>,
  }),

  getters: {
    /**
     * Get all store carts as an array
     */
    allCarts(state): StoreCart[] {
      return Object.values(state.carts);
    },

    /**
     * Get active/selected cart
     */
    activeCart(state): StoreCart | null {
      return state.activeCartSlug
        ? state.carts[state.activeCartSlug] || null
        : null;
    },

    /**
     * Get cart for a specific store
     */
    cartBySlug(state) {
      return (storeSlug: string): StoreCart | null => {
        return state.carts[storeSlug] || null;
      };
    },

    /**
     * Get total item count across all carts
     */
    totalItemCount(state): number {
      return Object.values(state.carts)
        .filter((cart): cart is StoreCart => Boolean(cart))
        .reduce((total, cart) => total + (Number(cart?.summary?.itemCount) || 0), 0);
    },

    /**
     * Get item count for active cart only
     */
    activeCartItemCount(state): number {
      if (!state.activeCartSlug) return 0;
      const cart = state.carts[state.activeCartSlug];
      return cart?.summary?.itemCount || 0;
    },

    /**
     * Get total amount across all carts
     */
    totalAmount(state): number {
      return Object.values(state.carts)
        .filter((cart): cart is StoreCart => Boolean(cart))
        .reduce((total, cart) => total + (Number(cart?.summary?.totalAmount) || 0), 0);
    },

    /**
     * Get active cart total
     */
    activeCartTotal(state): number {
      if (!state.activeCartSlug) return 0;
      const cart = state.carts[state.activeCartSlug];
      return Number(cart?.summary?.totalAmount) || 0;
    },

    /**
     * Get all items from active cart
     */
    activeCartItems(state): CartItem[] {
      if (!state.activeCartSlug) return [];
      const cart = state.carts[state.activeCartSlug];
      return cart?.items || [];
    },

    /**
     * Check if product is in any cart (or specific cart if storeSlug provided)
     */
    isInCart(state) {
      return (plu: string, storeSlug?: string): boolean => {
        if (storeSlug) {
          const cart = state.carts[storeSlug];
          return cart?.items?.some((i) => i.plu === plu) || false;
        }
        // Check all carts
        return Object.values(state.carts)
          .filter((cart): cart is StoreCart => Boolean(cart))
          .some((cart) => cart.items?.some((i) => i.plu === plu));
      };
    },

    /**
     * Get cart item quantity for a product
     */
    getCartItemQuantity(state) {
      return (plu: string, storeSlug?: string): number => {
        const pendingForStore = storeSlug ? state.pendingChanges[storeSlug] : null;
        if (storeSlug && pendingForStore) {
          const pendingTotal = Object.entries(pendingForStore)
            .filter(([key]) => key.startsWith(`${plu}::`))
            .reduce((sum, [, change]) => sum + (change?.quantity || 0), 0);
          if (pendingTotal || Object.keys(pendingForStore).some((key) => key.startsWith(`${plu}::`))) {
            return pendingTotal;
          }
        }
        if (storeSlug) {
          const cart = state.carts[storeSlug];
          const item = cart?.items?.find((i) => i.plu === plu);
          return item?.quantity || 0;
        }
        // Sum quantities across all carts
        return Object.values(state.carts)
          .filter((cart): cart is StoreCart => Boolean(cart))
          .reduce((total, cart) => {
            const item = cart.items?.find((i) => i.plu === plu);
            return total + (item?.quantity || 0);
          }, 0);
      };
    },

    /**
     * Get carts that have items (non-empty carts)
     */
    activeCarts(state): StoreCart[] {
      return Object.values(state.carts).filter((cart): cart is StoreCart => {
        const hasItems = Boolean(cart?.items && cart.items.length > 0);
        const changes = cart?.store?.slug ? state.pendingChanges[cart.store.slug] || {} : {};
        const hasPending = Object.keys(changes).length > 0;
        return Boolean(cart) && (hasItems || hasPending);
      });
    },

    /**
     * Check if any carts have items
     */
    hasItems(state): boolean {
      return Object.values(state.carts).some((cart) => {
        const hasItems = Boolean(cart?.items && cart.items.length > 0);
        const changes = cart?.store?.slug ? state.pendingChanges?.[cart.store.slug] || {} : {};
        const hasPending = Object.keys(changes).length > 0;
        return hasItems || Boolean(hasPending);
      });
    },

    /**
     * Get loading states
     */
    cartLoading(state) {
      return state.loading;
    },

    /**
     * Get error states
     */
    cartErrors(state) {
      return state.error;
    },

    /**
     * Check if a cart has staged (unsaved) quantity changes
     */
    hasPendingChanges(state) {
      return (storeSlug?: string): boolean => {
        if (storeSlug) {
          const changes = state.pendingChanges?.[storeSlug] || {};
          return Boolean(changes && Object.keys(changes).length > 0);
        }
        return Object.values(state.pendingChanges || {}).some(
          (changes) => changes && Object.keys(changes).length > 0
        );
      };
    },
  },

  actions: {
    /**
     * Set active cart by store slug
     */
    setActiveCart(storeSlug: string | null) {
      this.activeCartSlug = storeSlug;
    },

    /**
     * Persist cart snapshot to localStorage
     */
    persistSnapshot() {
      try {
        const snapshot: CartSnapshot = {
          carts: this.carts,
          activeCartSlug: this.activeCartSlug,
          ts: Date.now(),
        };
        localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
        this.persistedAt = snapshot.ts;
      } catch (error) {
        console.error("Failed to persist cart snapshot:", error);
      }
    },

    /**
     * Rehydrate cart from localStorage snapshot
     */
    rehydrateFromSnapshot(): boolean {
      try {
        const raw = localStorage.getItem(SNAPSHOT_KEY);
        if (!raw) return false;

        const snap: CartSnapshot = JSON.parse(raw);
        this.carts = Object.keys(snap.carts || {}).reduce((acc, slug) => {
          const normalized = normalizeCart((snap.carts as any)[slug] || null);
          if (normalized) {
            acc[slug] = normalized;
          }
          return acc;
        }, {} as Record<string, StoreCart>);
        this.activeCartSlug = snap.activeCartSlug || null;
        this.persistedAt = snap.ts || null;
        this.pendingChanges = {};
        return true;
      } catch (error) {
        console.error("Failed to rehydrate cart:", error);
        return false;
      }
    },

    /**
     * Stage a quantity change locally without hitting the API
     */
    stageItemQuantity(
      storeSlug: string,
      plu: string,
      quantity: number,
      product?: Partial<CartItem> & { price?: string | number; name?: string },
      instructions?: Instruction[]
    ): StoreCart | null {
      let cart = this.carts[storeSlug];

      // Create a lightweight local cart if none exists and product details are provided
      if (!cart && product) {
        cart = {
          id: storeSlug,
          store: {
            id: (product as any)?.storeId || storeSlug,
            name: (product as any)?.storeName || storeSlug,
            slug: storeSlug,
            isActive: true,
          } as any,
          items: [],
          summary: {
            itemCount: 0,
            subtotal: "0",
            deliveryFee: "0",
            totalAmount: "0",
            delivery: {
              available: true,
              fee: "0",
              freeEligible: true,
              radiusKm: 0,
              preparationTimeMinutes: 0,
            },
            payment: {
              onlinePayment: true,
              cashOnDelivery: false,
              cashOnPickup: true,
            },
            eligibleForFreeDelivery: false,
            minimumOrderValue: "0",
            freeDeliveryThreshold: "0",
            amountLeftForFreeDelivery: "0",
          },
          metadata: {
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            timezone: "UTC",
            hasSpecialInstructions: false,
          },
        } as StoreCart;
        this.carts[storeSlug] = cart;
      }

      if (!cart) return null;

      const items = [...(cart.items || [])];
      // Keep instruction sets distinct; only merge quantities for identical instruction signatures
      const effectiveInstructions =
        instructions ?? (product as any)?.instructions ?? [];
      const targetSignature = instructionSignature(effectiveInstructions);
      let itemIndex = items.findIndex(
        (i) =>
          i.plu === plu &&
          instructionSignature(i.instructions as Instruction[] | undefined) === targetSignature
      );
      // Do not fall back to any-PLU match when instructions differ; require matching signature

      if (itemIndex < 0) {
        const fallbackIndex = items.findIndex((i) => i.plu === plu);
        if (fallbackIndex >= 0) {
          itemIndex = fallbackIndex;
        }
      }

      if (!this.pendingChanges[storeSlug]) {
        this.pendingChanges[storeSlug] = {};
      }

      if (itemIndex < 0) {
        const pendingKey = buildPendingKey(plu, effectiveInstructions);
        // Allow creating a local staged item when product details are available
        if (quantity > 0 && product) {
          const unitPrice =
            Number((product as any)?.unitPrice || (product as any)?.price || 0) || 0;
          items.push({
            id: `${plu}`,
            plu,
            name: product.name || (product as any)?.productName || plu,
            unitPrice: unitPrice.toFixed(2),
            quantity,
            lineTotal: (unitPrice * quantity).toFixed(2),
            instructions: effectiveInstructions,
            addedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            imageUrl: (product as any)?.imageUrl || (product as any)?.image,
          } as any);

          this.pendingChanges[storeSlug][pendingKey] = {
            quantity,
            instructions: effectiveInstructions,
          };
          if (!this.newItems?.[storeSlug]) {
            this.newItems[storeSlug] = {};
          }
          this.newItems[storeSlug][plu] = true;
        } else {
          return cart;
        }
      } else {
        const currentItem = items[itemIndex];
        if (!currentItem) {
          return cart;
        }
        const currentQty = Number(currentItem.quantity) || 0;
        const delta = Math.max(quantity - currentQty, 0);
        if (quantity <= 0) {
          items.splice(itemIndex, 1);
          if (this.newItems?.[storeSlug]?.[plu]) {
            delete this.newItems[storeSlug][plu];
          }
          const pendingForStore = this.pendingChanges[storeSlug]
          if (pendingForStore) {
            Object.keys(pendingForStore).forEach((key) => {
              if (key.startsWith(`${plu}::`)) {
                delete pendingForStore[key];
              }
            });
          }
        } else {
          const currentItem = items[itemIndex]
          if (!currentItem) {
            return cart;
          }
          const unitPrice = Number(currentItem.unitPrice) || 0;
          const mergedInstructions =
            effectiveInstructions.length > 0
              ? mergeInstructionCounts(
                  currentItem.instructions as Instruction[] | undefined,
                  effectiveInstructions,
                  delta || 1
                )
              : quantity < currentQty && (currentItem.instructions as Instruction[] | undefined)?.length
                ? scaleInstructionCounts(
                    currentItem.instructions as Instruction[],
                    quantity / Math.max(currentQty, 1)
                  )
                : (currentItem.instructions as Instruction[] | undefined) || [];

          const updatedKey = buildPendingKey(plu, mergedInstructions);

          items[itemIndex] = {
            ...items[itemIndex],
            quantity,
            lineTotal: (unitPrice * quantity).toFixed(2),
            instructions: mergedInstructions,
          } as any;

          // Record the staged change with the merged instructions/signature
          const pendingForStore2 = this.pendingChanges[storeSlug]
          if (pendingForStore2) {
            Object.keys(pendingForStore2).forEach((key) => {
              if (key.startsWith(`${plu}::`)) {
                delete pendingForStore2[key];
              }
            });
            pendingForStore2[updatedKey] = {
              quantity,
              instructions: mergedInstructions,
            };
          }
        }
      }

      // Recalculate summary locally for snappier UI
      const subtotalNumber = items.reduce(
        (total, item) => total + (Number(item.lineTotal) || 0),
        0
      );
      const itemCount = items.reduce(
        (total, item) => total + (Number(item.quantity) || 0),
        0
      );
      const deliveryFee = Number(cart.summary?.deliveryFee || 0) || 0;
      const hasSpecialInstructions =
        items.some((item) => (item.instructions || []).length > 0) ||
        Object.values(this.pendingChanges[storeSlug] || {}).some(
          (change) => (change as StagedItemChange)?.instructions?.length
        );

      const nextCart = normalizeCart({
        ...cart,
        items,
        summary: {
          ...cart.summary,
          itemCount,
          subtotal: subtotalNumber.toFixed(2),
          totalAmount: (subtotalNumber + deliveryFee).toFixed(2),
        },
        metadata: {
          ...(cart.metadata || {}),
          hasSpecialInstructions,
        },
      }) as StoreCart;

      this.carts[storeSlug] = nextCart;
      this.persistSnapshot();
      return nextCart;
    },

    /**
     * Commit all staged changes for a store in a single batch of API calls
     */
    async commitCartChanges(storeSlug: string, showToast = false): Promise<StoreCart | null> {
      const staged = this.pendingChanges[storeSlug] || {};
      if (Object.keys(staged).length === 0 && !this.carts[storeSlug]) {
        return null;
      }

      // Coalesce concurrent commits per store to avoid duplicate API calls
      if (this.commitPromises?.[storeSlug]) {
        return this.commitPromises[storeSlug];
      }

      this.loading.update = true;
      this.error.update = "";

      const commitPromise = (async () => {
        try {
        const cart = this.carts[storeSlug];
        const itemsPayload =
          cart?.items
            ?.map((item) => {
              const qty = Number(item.quantity) || 0;
              if (qty <= 0) return null;
              const normalizedInstructions = normalizeInstructionsForApi(item.instructions as Instruction[] | undefined);
              return {
                plu: item.plu,
                name: item.name,
                unitPrice: Number(item.unitPrice) || 0,
                quantity: qty,
                instructions: normalizedInstructions.length ? normalizedInstructions : undefined,
              };
            })
            .filter(Boolean) || [];

        const { data: updatedCart } = await api.cart.replace(storeSlug, itemsPayload as any);
        const normalized = normalizeCart(updatedCart as StoreCart | null);

        if (normalized) {
          this.carts[storeSlug] = normalized;
        } else {
          delete this.carts[storeSlug];
        }

        delete this.pendingChanges[storeSlug];
        if (this.newItems?.[storeSlug]) {
          delete this.newItems[storeSlug];
        }
        this.persistSnapshot();

        if (showToast) {
          try {
            const { useUIStore } = await import("./useUIStore");
            useUIStore().showSuccess?.("Cart updated");
          } catch {
            // non-blocking
          }
        }

        return normalized || null;
        } catch (error: unknown) {
        this.error.update = getErrorMessage(error) || "Failed to update cart";
        throw error;
        } finally {
        this.loading.update = false;
        delete this.commitPromises[storeSlug];
        }
      })();

      this.commitPromises[storeSlug] = commitPromise;
      return commitPromise;
    },

    /**
     * Commit staged changes across all carts
     */
    async commitAllPendingChanges(): Promise<void> {
      const storeSlugs = Object.keys(this.pendingChanges || {});
      for (const slug of storeSlugs) {
        if (Object.keys(this.pendingChanges?.[slug] || {}).length > 0) {
          await this.commitCartChanges(slug, false);
        }
      }
    },


    /**
     * Fetch all carts from API
     */
    async fetchAllCarts(): Promise<StoreCart[]> {
      this.loading.fetchAll = true;
      this.error.fetchAll = "";

      try {
        const response = await api.cart.getAll();
        const carts: StoreCart[] = Array.isArray(response)
          ? response
          : response.data || [];

        // Convert array to keyed object
        this.carts = carts.reduce((acc, cart) => {
          const normalized = normalizeCart(cart);
          if (normalized) {
            acc[normalized.store.slug] = normalized;
          }
          return acc;
        }, {} as Record<string, StoreCart>);

        this.pendingChanges = {};

        this.persistSnapshot();
        return carts;
      } catch (error: unknown) {
        this.error.fetchAll = getErrorMessage(error) || "Failed to load carts";
        this.rehydrateFromSnapshot();
        return Object.values(this.carts);
      } finally {
        this.loading.fetchAll = false;
      }
    },

    /**
     * Fetch cart for a specific store
     */
    async fetchCart(storeSlug: string): Promise<StoreCart | null> {
      this.loading.fetch = true;
      this.error.fetch = "";

      try {
        const previousCart = this.carts[storeSlug];
        const { data: cart } = await api.cart.get(storeSlug);
        const fetchedCart = normalizeCart(cart as StoreCart | null);
        const normalizedPrevious = normalizeCart(previousCart || null);

        const hasFetchedItems =
          Array.isArray(fetchedCart?.items) && fetchedCart.items.length > 0;
        const hasPreviousItems =
          Array.isArray(normalizedPrevious?.items) &&
          normalizedPrevious.items.length > 0;

        // Only overwrite an existing non-empty cart if we got items back
        if (hasFetchedItems || !hasPreviousItems) {
          const nextCart = (fetchedCart || normalizedPrevious || null) as any;
          if (nextCart) {
            this.carts[storeSlug] = nextCart;
          }
          this.persistSnapshot();
        }

        // Return the most reliable cart we have
        return this.carts[storeSlug] || normalizedPrevious || null;
      } catch (error: unknown) {
        this.error.fetch = getErrorMessage(error) || "Failed to load cart";
        return this.carts[storeSlug] || null;
      } finally {
        this.loading.fetch = false;
      }
    },

    /**
     * Add item to a specific store's cart
     */
    async addItem(
      storeSlug: string,
      payload: AddItemPayload
    ): Promise<StoreCart | null> {
      const normalizeInstructionsForApi = (
        instructions?: Instruction[]
      ): Instruction[] => {
        if (!instructions || instructions.length === 0) return [];
        return instructions.map((inst) => ({
          CookingNum: inst.CookingNum || "",
          StockCode: inst.StockCode || "",
          CookingMsg: inst.CookingMsg || inst.text || "",
          Printer: inst.Printer || "",
          Qty: inst.Qty ?? inst.count ?? 1,
          Price: inst.Price ?? 0,
          count: inst.count ?? 1,
          Wait: inst.Wait ?? false,
        }));
      };

      const sanitizedInstructions = normalizeInstructionsForApi(
        payload.instructions as Instruction[] | undefined
      );

      const safePayload = {
        ...payload,
        name: payload.name || payload.plu,
        unitPrice: payload.unitPrice ?? 0.01,
        instructions: sanitizedInstructions.length ? sanitizedInstructions : undefined,
      };
      this.loading.add = true;
      this.error.add = "";

      try {
        const { data: updatedCart } = await api.cart.add(storeSlug, safePayload as any);
        // Save the updated cart to state so UI updates
        const normalized = normalizeCart(updatedCart as StoreCart | null);
        if (normalized) {
          this.carts[storeSlug] = normalized;
        }
        if (this.pendingChanges[storeSlug]) {
          delete this.pendingChanges[storeSlug];
        }
        this.persistSnapshot();
        return updatedCart as StoreCart;
      } catch (error: unknown) {
        // Handle authentication error with lazy import
        if (isAuthError(error)) {
          const { useUserStore } = await import("./useUserStore");
          const userStore = useUserStore();

          userStore.queueAuthAction({
            type: "custom",
            handler: async () => {
              await this.addItem(storeSlug, payload);
            },
          });

          userStore.showQuickLoginDialog({
            message: "Sign in to add items to your cart and checkout faster",
            action: "addToCart",
            url: `/v1/cart/${storeSlug}/items`,
          });

          return this.carts[storeSlug] || null;
        }

        this.error.add = getErrorMessage(error) || "Failed to add to cart";
        throw error;
      } finally {
        this.loading.add = false;
      }
    },

    /**
     * Compatibility wrapper: addToCart(product, storeSlug)
     * @deprecated Use addItem(storeSlug, payload) instead
     */
    async addToCart(
      product: {
        id?: string;
        productId?: string;
        plu?: string;
        name?: string;
        storeSlug?: string;
      },
      storeSlug?: string
    ): Promise<StoreCart | null> {
      const slug = storeSlug || product.storeSlug;
      if (!slug) {
        throw new Error("storeSlug is required for multi-store cart");
      }

      const payload: AddItemPayload = {
        plu: product.plu || product.id || product.productId || "",
        quantity: 1,
      };

      // Store the product name in local state for immediate UI updates
      // The backend will also return the name, but this ensures we have it immediately
      const existingCart = this.carts[slug];
      if (existingCart && product.name && existingCart.items) {
        // Check if item already exists in cart
        const existingItemIndex = existingCart.items.findIndex(
          (item) => item.plu === payload.plu
        );
        if (existingItemIndex >= 0 && existingCart.items[existingItemIndex]) {
          // Update existing item - the name should already be there from backend
          // but we ensure it's consistent with the product being added
          existingCart.items[existingItemIndex].name = product.name;
        }
      }

      return await this.addItem(slug, payload);
    },

    /**
     * Update item quantity in a specific cart
     */
    async updateItem(
      storeSlug: string,
      plu: string,
      quantity: number
    ): Promise<StoreCart | null> {
      this.loading.update = true;
      this.error.update = "";

      try {
        const { data: updatedCart } = await api.cart.update(storeSlug, plu, { quantity });
        // Save the updated cart to state so UI updates
        const normalized = normalizeCart(updatedCart as StoreCart | null);
        if (normalized) {
          this.carts[storeSlug] = normalized;
        }
        if (this.pendingChanges[storeSlug]) {
          delete this.pendingChanges[storeSlug];
        }
        this.persistSnapshot();
        return updatedCart as StoreCart;
      } catch (error: unknown) {
        this.error.update = getErrorMessage(error) || "Failed to update item";
        throw error;
      } finally {
        this.loading.update = false;
      }
    },

    /**
     * Compatibility wrapper: updateQuantity
     * @deprecated Use updateItem(storeSlug, plu, quantity) instead
     */
    async updateQuantity(
      storeSlug: string,
      payload: UpdateQuantityPayload
    ): Promise<StoreCart | null> {
      const newQty = (payload.item.quantity || 1) + (payload.change || 0);
      if (newQty <= 0) {
        return this.removeItem(storeSlug, payload.item.plu);
      }
      return this.updateItem(storeSlug, payload.item.plu, newQty);
    },

    /**
     * Remove item from a specific cart
     */
    async removeItem(
      storeSlug: string,
      plu: string
    ): Promise<StoreCart | null> {
      this.loading.remove = true;
      this.error.remove = "";

      try {
        const { data: updatedCart } = await api.cart.remove(storeSlug, plu);
        // Save the updated cart to state so UI updates
        const normalized = normalizeCart(updatedCart as StoreCart | null);
        if (normalized) {
          this.carts[storeSlug] = normalized;
        } else {
          delete this.carts[storeSlug];
        }
        if (this.pendingChanges[storeSlug]) {
          delete this.pendingChanges[storeSlug];
        }
        this.persistSnapshot();
        return updatedCart as StoreCart;
      } catch (error: unknown) {
        this.error.remove = getErrorMessage(error) || "Failed to remove item";
        throw error;
      } finally {
        this.loading.remove = false;
      }
    },

    /**
     * Compatibility wrapper: removeFromCart
     * @deprecated Use removeItem(storeSlug, plu) instead
     */
    async removeFromCart(
      plu: string,
      storeSlug?: string
    ): Promise<StoreCart | null> {
      if (!storeSlug && !this.activeCartSlug) {
        throw new Error("storeSlug is required when no active cart is set");
      }
      const slug = storeSlug || this.activeCartSlug!;
      return this.removeItem(slug, plu);
    },

    /**
     * Clear entire cart for a specific store
     */
    async clearCart(storeSlug: string): Promise<StoreCart | null> {
      this.loading.clear = true;
      this.error.clear = "";

      try {
        const { data: clearedCart } = await api.cart.clear(storeSlug);
        const cartData = (clearedCart as StoreCart) || null;

        // If the API returns an empty cart, remove it from state; otherwise keep the updated cart
        if (cartData && cartData.items && cartData.items.length > 0) {
          const normalized = normalizeCart(cartData);
          if (normalized) {
            this.carts[storeSlug] = normalized;
          }
        } else {
          delete this.carts[storeSlug];
          if (this.activeCartSlug === storeSlug) {
            this.activeCartSlug = null;
          }
        }
        delete this.pendingChanges[storeSlug];

        this.persistSnapshot();
        return cartData;
      } catch (error: unknown) {
        this.error.clear = getErrorMessage(error) || "Failed to clear cart";
        return null;
      } finally {
        this.loading.clear = false;
      }
    },

    /**
     * Fetch delivery quote for a selected address
     */
    async fetchDeliveryQuote(
      storeSlug: string,
      shippingAddressId: string
    ): Promise<DeliveryQuote | null> {
      this.loading.deliveryQuote = true
      this.error.deliveryQuote = ""

      try {
        const result = await api.orders.deliveryQuote(storeSlug, shippingAddressId)
        if (result.status === "ERROR" || !result.data) {
          throw new Error(result.error?.message || "Failed to calculate delivery fee")
        }
        return result.data as DeliveryQuote
      } catch (error: unknown) {
        this.error.deliveryQuote = getErrorMessage(error) || "Failed to calculate delivery fee"
        return null
      } finally {
        this.loading.deliveryQuote = false
      }
    },

    /**
     * Checkout a specific cart
     */
    async checkout(storeSlug: string, payload: CheckoutPayload): Promise<void> {
      this.loading.checkout = true;
      this.error.checkout = "";

      try {
        // Ensure any staged quantity changes are persisted before checkout
        await this.commitCartChanges(storeSlug);

        const result = await api.cart.process(storeSlug, payload);

        // Check if the request failed
        if (result.status === "ERROR" || !result.data) {
          throw new Error(result.error?.message || "Checkout failed");
        }

        // Successfully processed - clear the cart
        await this.clearCart(storeSlug);
      } catch (error: unknown) {
        this.error.checkout = getErrorMessage(error) || "Checkout failed";
        throw error;
      } finally {
        this.loading.checkout = false;
      }
    },
  },
});
